import {
	Box,
	Button,
	Card,
	CardContent,
	Checkbox,
	Dialog,
	DialogContent,
	FormControl,
	FormControlLabel,
	Grid,
	IconButton,
	Menu,
	MenuItem,
	Radio,
	RadioGroup,
	Select,
	Skeleton,
	Stack,
	TextField,
	Typography,
} from '@mui/material';

import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MainGrid from '../../components/MainGrid';
import { useNavigate } from 'react-router-dom';
import {
	getAccessToken,
	getElderHomeId,
	getUserEmail,
	getUserId,
	getUserName,
} from '../../Utils/AuthUtil';
import { useEffect, useState } from 'react';
import { getEnabledUserFeatures } from '../../context/MainProvider';
import { FEATURE_TYPES } from '../../Utils/Enums';
import {
	fetchCQCReportData,
	generateNotificationData,
	getResidentIds,
} from '../../services/ElderService';
import { toast } from 'react-toastify';
import ToastBanner from '../../components/ToastBanner';
import { CloseIcon, StarIcon } from '../../assets/icons/Icons';
import { helperText } from '../../assets/theme/Theme';
import { dark, surface, teal } from '../../assets/theme/Colors';
import AddIcon from '@mui/icons-material/Add';
import Dropzone from 'react-dropzone';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { onExtractCQCTemplate } from '../../services/CarePlanServices';
import { getCQCTemplate, storeCQCTemplate } from '../../services/UserService';

const initialErrorState = { status: false, msg: '' };

const cqcCategories = [
	{
		id: 'change_in_registered_details',
		name: 'Change in Registered Details',
	},
	{
		id: 'events_and_incidents',
		name: 'Events & Incidents',
	},
];

const providerDetails = {
	providerNotificationReference: 'REF-ABS-2026-0018',
	providerName: 'North field Care Services Ltd',
	cqcProviderNumber: '1-0001234567',
	formFilledBy: '',
	dateSubmitted: '23/04/2026',
	contactForMoreInformation: 'Daniel Mercer',
	telephoneNumber: '01632 960421',
	emailAddress: '',
	affectedLocationScope: 'one_location',
	locationNameAndAddress:
		'North field Residential Home, 14 Willow Park Road, Ashford',
	locationPostcode: 'AS1 4PL',
	cqcLocationNumber: '1-0007654321',
};

function InputElement(
	type,
	name,
	value,
	onChange,
	label,
	options = [],
	placeholder = '',
	disabled = false,
	error = null,
	...rest
) {
	const labelStyle = {
		fontSize: 12,
		fontWeight: 500,
		mb: 1,
		color: '#334155',
	};

	const textFieldStyle = {
		'& .MuiInputBase-input': {
			fontSize: 13,
		},
		'& .MuiOutlinedInput-root': {
			height: 40,
		},
	};

	const textAreaStyle = {
		'& .MuiInputBase-input': {
			fontSize: 13,
		},
	};

	const renderLabel = () => <Typography sx={labelStyle}>{label}</Typography>;

	switch (type) {
		case 'text':
		case 'email':
		case 'number':
		case 'password':
		case 'date':
			return (
				<div>
					{renderLabel()}
					<TextField
						fullWidth
						size="small"
						type={type}
						name={name}
						value={value}
						onChange={e => onChange(name, e.target.value)}
						placeholder={placeholder}
						sx={textFieldStyle}
						InputProps={{ readOnly: disabled }}
						error={error?.status}
						{...rest}
					/>
				</div>
			);

		case 'textarea':
			return (
				<div>
					{renderLabel()}
					<TextField
						fullWidth
						multiline
						minRows={3}
						name={name}
						value={value}
						onChange={e => onChange(name, e.target.value)}
						placeholder={placeholder}
						sx={textAreaStyle}
						InputProps={{ readOnly: disabled }}
						error={error?.status}
						{...rest}
					/>
				</div>
			);

		case 'checkbox':
			return (
				<div>
					{renderLabel()}
					{options.map(opt => {
						const optionValue =
							typeof opt === 'string' ? opt : opt.value;
						const optionLabel =
							typeof opt === 'string' ? opt : opt.label;

						return (
							<FormControlLabel
								key={optionValue}
								control={
									<Checkbox
										checked={value?.includes(optionValue)}
										onChange={e => {
											let newValue = value
												? [...value]
												: [];

											if (e.target.checked) {
												newValue.push(optionValue);
											} else {
												newValue = newValue.filter(
													v => v !== optionValue,
												);
											}

											onChange(name, newValue);
										}}
										sx={{
											color: teal,
											'&.Mui-checked': {
												color: teal,
											},
											padding: 0,
											paddingRight: 1,
											paddingLeft: 1,
										}}
									/>
								}
								label={optionLabel}
								sx={{
									'& .MuiFormControlLabel-label': {
										fontSize: 14,
									},
									padding: 0,
								}}
							/>
						);
					})}
				</div>
			);

		case 'select':
			return (
				<div>
					{renderLabel()}
					<FormControl fullWidth size="small">
						<Select
							name={name}
							value={value}
							onChange={e => onChange(name, e.target.value)}>
							{options.map(opt => {
								const optionValue =
									typeof opt === 'string' ? opt : opt.value;
								const optionLabel =
									typeof opt === 'string' ? opt : opt.label;

								return (
									<MenuItem
										key={optionValue}
										value={optionValue}>
										{optionLabel}
									</MenuItem>
								);
							})}
						</Select>
					</FormControl>
				</div>
			);

		case 'radio':
			return (
				<div>
					{renderLabel()}
					<FormControl>
						<RadioGroup
							name={name}
							value={value}
							sx={{ flexDirection: 'row' }}
							onChange={e => onChange(name, e.target.value)}>
							{options.map(opt => {
								const value =
									typeof opt === 'string' ? opt : opt.value;
								const label =
									typeof opt === 'string' ? opt : opt.label;

								return (
									<FormControlLabel
										key={value}
										value={value}
										control={
											<Radio
												size="small"
												sx={{
													color: teal,
													'&.Mui-checked': {
														color: teal,
													},
													cursor: 'pointer',
												}}
											/>
										}
										label={label}
										sx={{
											'& .MuiFormControlLabel-label': {
												fontSize: 14,
											},
											padding: 0,
										}}
									/>
								);
							})}
						</RadioGroup>
					</FormControl>
				</div>
			);

		default:
			return null;
	}
}

export default function CQCNotification() {
	const homeId = getElderHomeId();
	const token = getAccessToken();
	const [id, setId] = useState(null);
	const [residentIds, setResidentIds] = useState([]);
	const [idError, setIdError] = useState(initialErrorState);
	const [templates, setTemplates] = useState([]);
	const [selectedTemplate, setSelectedTemplate] = useState(null);
	const [selectedTemplateName, setSelectedTemplateName] = useState(null);
	const [tempInstructions, setTempInstructions] = useState([]);
	const [onFetchedPreFilledData, setOnFetchedPreFilledData] = useState(false);
	const [onShowTemplateResidentOpt, setOnShowTemplateResidentOpt] =
		useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [loadingMsg, setLoadingMsg] = useState('');
	const [isGenerationComplete, setIsGenerationComplete] = useState(false);
	const [residentFetchedIds, setResidentFetchedIds] = useState([]);
	const [fileData, setFileData] = useState(null);
	const [txtError, setTxtError] = useState(initialErrorState);
	const [anchorEl, setAnchorEl] = useState(null);
	const [instructions, setInstructions] = useState('');
	const [instructionError, setInstructionError] = useState(initialErrorState);
	const [reportData, setReportData] = useState({});
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [name, setName] = useState('');
	const [nameError, setNameError] = useState(initialErrorState);
	const [category, setCategory] = useState(null);
	const [categoryError, setCategoryError] = useState(initialErrorState);
	const [tempInstruction, setTempInstruction] = useState('');
	const [tempInstructionError, setTempInstructionError] =
		useState(initialErrorState);
	const [isFetchingTemplates, setIsFetchingTemplates] = useState(false);

	useEffect(() => {
		fetchResidentIds();
		fetchTemplates();
	}, []);

	async function fetchResidentIds() {
		const response = await getResidentIds(homeId, token);

		if (response.message === 'Success') {
			const newIds = response.content.map(item =>
				item.isFromExternalApi
					? item.name
					: `${item.elderId} (${item.name})`,
			);

			setResidentFetchedIds(response.content);
			setResidentIds(newIds);
		} else {
			toast.error(response.message);
		}
	}

	async function fetchTemplates() {
		setIsFetchingTemplates(true);

		const response = await getCQCTemplate(homeId, token);
		if (response.message === 'Success') {
			setTemplates(response.content);
		} else {
			toast.error(response.message);
		}

		setIsFetchingTemplates(false);
	}

	function validateUploadInputs() {
		let isValid = true;

		if (!id || id === '') {
			setIdError({ status: true, msg: 'Select Resident' });
			isValid = false;
		}

		if (!instructions || instructions === '') {
			setInstructionError({
				status: true,
				msg: 'Please Provide Details',
			});
			isValid = false;
		}

		return isValid;
	}

	/**
	 * Step 01 - Generating report to display report and allow user to edit/update
	 */
	async function getReportData() {
		if (!validateUploadInputs()) return;

		setLoadingMsg('Preparing Report...');
		setIsLoading(true);
		const selectedElder = residentFetchedIds.find(
			item => item.elderId === id.split(' ')[0] || item.name === id,
		);

		const d = new Date();
		const formatted = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;

		const providerObj = {
			...providerDetails,
			formFilledBy: getUserName(),
			emailAddress: getUserEmail(),
			dateSubmitted: formatted,
		};

		const provider = Object.values(providerObj).join('\n');

		const templateString =
			templates.find(item => item.id === selectedTemplate).template ??
			'{}';

		const template = JSON.parse(templateString);

		const body = {
			...template,
			elder_id: selectedElder.id,
			input_text: instructions,
			client_id: getElderHomeId(),
			provider_details: provider,
		};

		const response = await fetchCQCReportData(body);

		if (!response.error) {
			setReportData(response?.filled_document_schema ?? {});
			setOnFetchedPreFilledData(true);
		} else {
			toast.error('Error filling cqc report');
		}

		setIsLoading(false);
		setLoadingMsg('');
	}

	/**
	 * Step 02 - Generating report based on user selected document type (final step)
	 */
	async function onFetchTemplateData(docType = 'docx') {
		setAnchorEl(null);
		setLoadingMsg('Generating Document...');
		setIsLoading(true);

		try {
			const { blob, filename } = await fetchTemplateData(docType);

			downloadFile(blob, filename);
			onResetState();
			toast.success('Document downloaded!');
		} catch (error) {
			console.error(error);
			toast.error(error.message);
		} finally {
			setIsLoading(false);
			setLoadingMsg('');
		}
	}

	/**
	 * Step 02 - Generating report on both document types (final step)
	 */
	async function generateBothTempData() {
		setAnchorEl(null);
		setLoadingMsg('Generating Document...');
		setIsLoading(true);

		try {
			const pdf = await fetchTemplateData('pdf');
			downloadFile(pdf.blob, pdf.filename);

			const docx = await fetchTemplateData('docx');
			downloadFile(docx.blob, docx.filename);

			onResetState();
			toast.success('Both documents downloaded!');
		} catch (error) {
			toast.error(error.message);
		} finally {
			setIsLoading(false);
			setLoadingMsg('');
		}
	}

	async function fetchTemplateData(docType) {
		const selectedElder = residentFetchedIds.find(
			item => item.elderId === id.split(' ')[0] || item.name === id,
		);

		const template =
			templates.find(item => item.id === selectedTemplate).template ??
			'{}';

		const templateJson = JSON.parse(template);

		const data = {
			...templateJson,
			elder_id: selectedElder.elderId,
			template_id: templateJson.template_id,
			document_schema: { ...reportData },
			return_download_docx: true,
			file_format: docType,
		};

		const tempTitle = templates.find(
			item => item.id === selectedTemplate,
		).templateName;

		const fileName = `${tempTitle} - ${selectedElder.name}`;

		return generateNotificationData(data, docType, fileName);
	}

	function downloadFile(blob, filename) {
		const url = URL.createObjectURL(blob);

		const a = document.createElement('a');
		a.href = url;
		a.download = filename;

		document.body.appendChild(a);
		a.click();
		a.remove();

		URL.revokeObjectURL(url);
	}

	function onChangeSelection(e, value) {
		setSelectedTemplate(value.id);
		setSelectedTemplateName(value.templateName);
		onFetchInstructions(value.templateName);
		setOnShowTemplateResidentOpt(true);
	}

	function onChangeSelectionElder(value) {
		setId(value);
		setIdError(initialErrorState);
	}

	function onFetchInstructions(templateName) {
		const template = templates.find(
			item => item.templateName === templateName,
		);
		setTempInstructions(template.templateInstruction);
	}

	function readTextFile(e) {
		const ext = e[0].path.split('.').pop().toLowerCase();

		if (!['pdf', 'docx'].includes(ext)) {
			setTxtError({ status: true, msg: 'Invalid file format.' });
			return;
		}

		if (e[0] && e[0].size === 0) {
			setTxtError({
				status: true,
				msg: 'The selected file is empty.',
			});
			return;
		}

		if (e[0].size && e[0].size > 5242880) {
			setTxtError({
				status: true,
				msg: 'Maximum upload file size is 5MB.',
			});
			return;
		}

		setTxtError({ status: false, msg: '' });
		setFileData(e[0]);
	}

	function handleClose() {
		setAnchorEl(null);
	}

	// Updates a deep nested object safely using a path array
	function updateData(obj, path, value) {
		if (!obj) obj = {};

		const [key, ...rest] = path; // Removing the first element

		if (rest.length === 0) {
			return {
				...obj,
				[key]: {
					...(obj[key] || {}),
					value,
				},
			};
		}

		return {
			...obj,
			[key]: updateData(obj[key] || {}, rest, value),
		};
	}

	const handleOnChange = (path, value) => {
		setReportData(prev => updateData(prev, path, value));
	};

	function validateInputs() {
		let isValid = true;
		if (!name || name === '') {
			setNameError({ status: true, msg: 'Enter template name' });
			isValid = false;
		}

		if (!category || category === '') {
			setCategoryError({ status: true, msg: 'Select category' });
			isValid = false;
		}

		if (!tempInstruction || tempInstruction === '') {
			setTempInstructionError({
				status: true,
				msg: 'Enter instructions',
			});
			isValid = false;
		}

		if (!fileData) {
			setTxtError({ status: true, msg: 'Upload the template file' });
			isValid = false;
		}

		return isValid;
	}

	async function onExtractTemplate() {
		if (!validateInputs()) {
			return;
		}

		const formData = new FormData();
		formData.append('docx_file', fileData);
		formData.append('notification_category', category);
		formData.append('document_name', name);

		setLoadingMsg('Uploading cqc template...');
		setIsLoading(true);

		const response = await onExtractCQCTemplate(formData);

		if (!response.error) {
			const templateJson = response.results[0];
			if (Object.keys(templateJson).length > 0) {
				onStoreCQCTemplate(templateJson);
			} else {
				toast.error('Received empty template data');
				setIsLoading(false);
			}
		} else {
			toast.error(response.message);
			setIsLoading(false);
		}
	}

	async function onStoreCQCTemplate(templateJson) {
		setLoadingMsg('Storing template...');
		setIsLoading(true);

		const body = {
			addedBy: getUserId(),
			templateName: name,
			templateCategory: category,
			templateInstruction: tempInstruction,
			template: JSON.stringify(templateJson),
		};

		const response = await storeCQCTemplate(body);

		if (!response.error) {
			toast.success('Template stored successfully', {
				style: {
					background: '#4F9E4E',
				},
			});
			onCloseModalAndState();
			fetchTemplates();
		} else {
			toast.error(response.message);
		}

		setIsLoading(false);
		setLoadingMsg('');
	}

	function onCloseModalAndState() {
		setIsModalOpen(false);
		setFileData(null);
		setTempInstruction('');
		setName('');
		setCategory(null);
		setNameError(initialErrorState);
		setTxtError(initialErrorState);
		setCategoryError(initialErrorState);
		setTempInstructionError(initialErrorState);
	}

	function onResetState() {
		setOnShowTemplateResidentOpt(false);
		setFileData(null);
		setId(null);
		setSelectedTemplate('');
		setInstructions('');
		setTempInstructions([]);
		setReportData({});
		setOnFetchedPreFilledData(false);
	}

	function onRenderFields() {
		return Object.entries(reportData).map(([sectionTitle, fields]) => (
			<Card
				key={sectionTitle}
				style={{ width: '100%', marginBottom: 20 }}>
				<CardContent>
					<Typography
						sx={{
							fontSize: 14,
							fontWeight: 600,
							color: dark,
							mb: 1,
						}}>
						{sectionTitle}
					</Typography>

					{Object.entries(fields).map(([key, field]) => {
						// nested group (like location block)
						if (field.field_type === undefined) {
							return (
								<Grid
									key={key}
									style={{
										marginBottom: 10,
									}}>
									<Typography
										sx={{
											fontSize: 13,
											fontWeight: 600,
											color: dark,
											my: 1,
										}}>
										{key}
									</Typography>

									{Object.entries(field).map(
										([subKey, subField]) => (
											<Grid
												key={subField.field_id}
												sx={{ mt: 1, ml: 1 }}>
												{InputElement(
													subField.field_type,
													subKey,
													subField.value,
													(fieldName, value) =>
														handleOnChange(
															[
																sectionTitle,
																key,
																fieldName,
															],
															value,
														),
													subKey,
													subField.options ?? [],
												)}
											</Grid>
										),
									)}
								</Grid>
							);
						}

						return (
							<Grid key={field.field_id} sx={{ mt: 1 }}>
								{InputElement(
									field.field_type,
									key,
									field.value,
									(fieldName, value) =>
										handleOnChange(
											[sectionTitle, fieldName],
											value,
										),
									key,
									field.options ?? [],
								)}
							</Grid>
						);
					})}
				</CardContent>
			</Card>
		));
	}

	return (
		<MainGrid>
			<Box
				sx={{
					width: '100%',
				}}>
				{/* Header */}
				<Box
					sx={{
						mb: 4,
						flexDirection: 'row',
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}>
					<Box>
						<Typography
							sx={{
								fontSize: '22px',
								fontWeight: 600,
								color: dark,
								mb: 0.5,
							}}>
							CQC Notification Reports
						</Typography>

						<Typography
							sx={{
								fontSize: 14,
								color: '#6b8888',
							}}>
							Select a template — resident data pre-filled from
							profiles, review before generating
						</Typography>
					</Box>
					<Button
						variant="contained"
						onClick={() => setIsModalOpen(true)}
						startIcon={<AddIcon />}
						sx={{
							background: teal,
							color: dark,
							fontWeight: 600,
							borderRadius: 2,
							height: 30,
							fontSize: 12,
							textTransform: 'none',
							px: 2,
							'&:hover': {
								background: '#00b0a0',
							},
						}}>
						New Template
					</Button>
				</Box>

				<Grid container spacing={2}>
					{/* Left */}
					<Grid container width={'100%'}>
						<Card
							sx={{
								borderRadius: '18px',
								border: '1px solid #d8e8e8',
								boxShadow: 'none',
								width: '100%',
							}}>
							<CardContent sx={{ p: 3 }}>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: 1,
										mb: 3,
									}}>
									<DescriptionRoundedIcon
										sx={{
											fontSize: 18,
											color: teal,
										}}
									/>

									<Typography
										sx={{
											fontSize: 14,
											fontWeight: 700,
											color: dark,
										}}>
										Report Templates
									</Typography>
								</Box>

								<Stack spacing={1}>
									{isFetchingTemplates ? (
										<Skeleton
											variant="rectangular"
											height={60}
											sx={{
												borderRadius: '10px',
											}}
										/>
									) : templates.length > 0 ? (
										templates.map(item => (
											<Box
												key={item.id}
												onClick={() =>
													onChangeSelection(
														null,
														item,
													)
												}
												sx={{
													pointerEvents:
														onShowTemplateResidentOpt
															? 'none'
															: 'auto',
													opacity:
														onShowTemplateResidentOpt
															? 0.5
															: 1,
													border:
														selectedTemplate ===
														item.id
															? '1px solid #b3ede8'
															: '1px solid #d8e8e8',
													background:
														selectedTemplate ===
														item.id
															? '#eefcf9'
															: '#fff',
													borderRadius: '12px',
													p: 1.5,
													display: 'flex',
													alignItems: 'center',
													gap: 1.5,
													cursor: 'pointer',
													transition: '0.2s',
													'&:hover': {
														borderColor: teal,
														background: '#eefcf9',
													},
												}}>
												<Box
													sx={{
														width: 36,
														height: 36,
														borderRadius: '10px',
														background:
															selectedTemplate ===
															item.id
																? 'rgba(0,196,180,0.15)'
																: surface,
														display: 'flex',
														alignItems: 'center',
														justifyContent:
															'center',
														color:
															selectedTemplate ===
															item.id
																? teal
																: '#6b8888',
													}}>
													{
														<DescriptionRoundedIcon
															sx={{
																fontSize: 18,
															}}
														/>
													}
												</Box>

												<Box sx={{ flex: 1 }}>
													<Typography
														sx={{
															fontSize: 13,
															fontWeight: 600,
															color: '#1a2424',
														}}>
														{item.templateName}
													</Typography>

													<Typography
														sx={{
															fontSize: 11,
															color: '#6b8888',
														}}>
														{item.templateCategory}
													</Typography>
												</Box>

												{selectedTemplate ===
													item.id && (
													<CheckCircleRoundedIcon
														sx={{
															fontSize: 18,
															color: teal,
														}}
													/>
												)}
											</Box>
										))
									) : (
										<Card
											sx={{
												borderRadius: '18px',
												border: '1px solid #d8e8e8',
												boxShadow: 'none',
												alignItems: 'center',
												width: '100%',
											}}>
											<CardContent
												sx={{
													p: 3,
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													minHeight: 50,
													color: '#6b8888',
													fontSize: 13,
													fontWeight: 500,
												}}>
												No templates available.
											</CardContent>
										</Card>
									)}
								</Stack>
							</CardContent>
						</Card>
					</Grid>

					<Grid container>
						{onShowTemplateResidentOpt && (
							<Card
								sx={{
									borderRadius: '18px',
									border: '1px solid #d8e8e8',
									boxShadow: 'none',
									width: '100%',
									marginTop: '10px',
								}}>
								<CardContent sx={{ p: 3 }}>
									{/* Header */}
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 1,
											mb: 2,
										}}>
										<WarningAmberRoundedIcon
											sx={{
												fontSize: 18,
												color: teal,
											}}
										/>

										<Typography
											sx={{
												fontSize: 14,
												fontWeight: 700,
												color: dark,
											}}>
											{`${selectedTemplateName} Notification`}
										</Typography>
									</Box>

									{/* Fields */}
									<Grid container>
										<Typography
											sx={{
												fontSize: 12,
												fontWeight: 600,
												color: '#6b8888',
												mb: 0.7,
											}}>
											Resident
										</Typography>
										<Select
											fullWidth
											size="small"
											disabled={onFetchedPreFilledData}
											// value={id}
											onChange={e =>
												onChangeSelectionElder(
													e.target.value,
												)
											}
											defaultValue="Select Resident"
											error={idError.status}
											sx={{
												fontSize: 13,
												borderRadius: '10px',
												background: '#fff',
											}}>
											<MenuItem
												disabled
												sx={{ fontSize: 12 }}
												value={'Select Resident'}>
												Select Resident
											</MenuItem>
											{residentIds.map((item, index) => (
												<MenuItem
													key={index}
													value={item}
													sx={{ fontSize: '12px' }}>
													{item}
												</MenuItem>
											))}
										</Select>
										<Typography style={helperText}>
											{idError.msg}
										</Typography>
									</Grid>

									<Grid>
										<Grid
											container
											sx={{
												backgroundColor: '#FAFAFA',
												marginBottom: '8px',
												padding: '16px 8px 16px',
												flexDirection: 'column',
												display: 'flex',
											}}>
											<Box
												sx={{
													display: 'flex',
													flexDirection: 'column',
													gap: 1,
												}}>
												<Typography
													component="div"
													sx={{
														fontSize: '13px',
														color: '#324054',
														fontWeight: 500,
													}}>
													{tempInstructions}
												</Typography>
											</Box>
										</Grid>
										<TextField
											fullWidth
											multiline
											minRows={3}
											value={instructions}
											onChange={e => {
												setInstructions(e.target.value);
												setInstructionError(
													initialErrorState,
												);
											}}
											disabled={onFetchedPreFilledData}
											placeholder="e.g. Give me a brief summary of all residents with dementia who had any incidents in the last 2 weeks, including what happened and what actions were taken…"
											sx={{
												mb: 1.2,
												'& .MuiOutlinedInput-root': {
													borderRadius: '5px',
													backgroundColor: '#fff',
													alignItems: 'flex-start',
													pr: 1,
												},

												'& textarea': {
													fontSize: '13px',
													lineHeight: 1.6,
													color: '#334155',
												},
											}}
											error={instructionError.status}
										/>
										{instructionError.status && (
											<Typography sx={helperText}>
												{instructionError.msg}
											</Typography>
										)}
										<Button
											onClick={getReportData}
											disabled={onFetchedPreFilledData}
											sx={{
												py: 1.2,
												px: 2,
												borderRadius: '10px',
												background: teal,
												color: dark,
												fontSize: 13,
												fontWeight: 600,
												textTransform: 'none',
												boxShadow: 'none',
												'&:hover': {
													background: '#00b0a0',
													boxShadow: 'none',
												},
												marginTop: '5px',
												paddingX: '20px',
											}}>
											Prepare Report
										</Button>
									</Grid>

									{Object.keys(reportData).length > 0 && (
										<Grid
											container
											spacing={2}
											marginTop={1}
											padding={'16px 8px 16px'}>
											{onRenderFields()}
										</Grid>
									)}
									{Object.keys(reportData).length > 0 && (
										<Grid container>
											<Button
												startIcon={<AutoAwesomeIcon />}
												onClick={e =>
													setAnchorEl(e.currentTarget)
												}
												disabled={isLoading}
												sx={{
													mt: '10px',
													py: 1.2,
													px: 2,
													borderRadius: 2,
													textTransform: 'none',
													background: teal,
													color: '#083838',
													fontWeight: 600,
													fontSize: 13,
													boxShadow: 'none',
													'&:hover': {
														background: '#00b2a3',
														boxShadow: 'none',
													},
												}}>
												Generate Report
											</Button>
											<Menu
												anchorEl={anchorEl}
												open={Boolean(anchorEl)}
												onClose={handleClose}
												anchorOrigin={{
													vertical: 'bottom',
													horizontal: 'right',
												}}
												transformOrigin={{
													vertical: 'top',
													horizontal: 'right',
												}}
												PaperProps={{
													sx: {
														mt: 0,
														width: '180px',
													},
												}}>
												<MenuItem
													sx={{ fontSize: '14px' }}
													onClick={() =>
														onFetchTemplateData(
															'pdf',
														)
													}>
													Generate (.PDF)
												</MenuItem>
												<MenuItem
													sx={{ fontSize: '14px' }}
													onClick={() =>
														onFetchTemplateData(
															'docx',
														)
													}>
													Generate (.DOCX)
												</MenuItem>
												<MenuItem
													sx={{ fontSize: '14px' }}
													onClick={
														generateBothTempData
													}>
													Generate Both
												</MenuItem>
											</Menu>
										</Grid>
									)}
								</CardContent>
							</Card>
						)}
					</Grid>
				</Grid>
			</Box>
			<Dialog
				open={isModalOpen}
				fullWidth
				maxWidth="lg"
				onClose={() => setIsModalOpen(false)}
				scroll="body"
				PaperProps={{
					style: {
						backgroundColor: 'transparent',
						boxShadow: 'none',
					},
				}}>
				<DialogContent
					style={{
						padding: '0px 0px 20px 0px',
					}}>
					<Card
						sx={{
							borderRadius: '20px',
							border: '1px solid #e2e8f0',
							boxShadow: 'none',
							width: '100%',
							backgroundColor: '#fff',
						}}>
						<CardContent sx={{ p: 3 }}>
							<Grid
								container
								justifyContent={'space-between'}
								alignItems={'center'}>
								<Typography
									sx={{
										fontSize: '13px',
										fontWeight: 600,
										color: '#0f172a',
									}}>
									Add New CQC Template
								</Typography>
								<IconButton onClick={onCloseModalAndState}>
									<CloseIcon />
								</IconButton>
							</Grid>
							<Grid>
								<Dropzone
									onDrop={acceptedFiles =>
										readTextFile(acceptedFiles)
									}>
									{({
										getRootProps,
										getInputProps,
										isDragActive,
									}) => (
										<Box
											{...getRootProps()}
											sx={{
												border: `2px dashed ${txtError?.status ? 'red' : '#b3ede8'}`,
												borderRadius: '12px',
												p: '28px 20px',
												textAlign: 'center',
												background: isDragActive
													? '#d0f5f1'
													: '#e6f9f7',
												cursor: 'pointer',
												transition: 'all 0.15s ease',
												'&:hover': {
													background: '#d0f5f1',
													borderColor: teal,
												},
											}}>
											<input {...getInputProps()} />

											<UploadFileIcon
												sx={{
													fontSize: 32,
													color: teal,
													mb: 1,
												}}
											/>
											{!fileData ? (
												<>
													<Typography
														sx={{
															fontSize: 14,
															fontWeight: 600,
															color: dark,
															mb: 0.5,
														}}>
														Upload CQC template file
													</Typography>

													<Typography
														sx={{
															fontSize: 12,
															color: '#6b8888',
															lineHeight: 1.5,
														}}>
														Upload files in .pdf or
														.docx format.
													</Typography>
												</>
											) : (
												<Typography
													sx={{
														fontSize: 14,
														fontWeight: 600,
														color: dark,
														mb: 0.5,
													}}>
													{fileData.name}
												</Typography>
											)}
										</Box>
									)}
								</Dropzone>
								{txtError?.status && (
									<Typography style={helperText}>
										{txtError?.msg}
									</Typography>
								)}

								<Stack>
									<Box>
										<Typography
											sx={{
												fontSize: '12px',
												fontWeight: 500,
												color: '#6b8888',
												my: 0.8,
											}}>
											CQC Category
										</Typography>
										<TextField
											select
											fullWidth
											size="small"
											onChange={e => {
												setCategory(e.target.value);
												setCategoryError(
													initialErrorState,
												);
											}}
											defaultValue="Select Category"
											error={categoryError?.status}
											sx={{
												'& .MuiOutlinedInput-root': {
													borderRadius: '12px',
													fontSize: '13px',
													backgroundColor: '#fff',
												},
											}}>
											<MenuItem
												value="Select Category"
												disabled
												sx={{ fontSize: '12px' }}>
												Select Category
											</MenuItem>
											{cqcCategories.map(
												(item, index) => (
													<MenuItem
														key={index}
														value={item.name}
														sx={{
															fontSize: '12px',
														}}>
														{item.name}
													</MenuItem>
												),
											)}
										</TextField>
									</Box>
									{categoryError?.status && (
										<Typography style={helperText}>
											{categoryError?.msg}
										</Typography>
									)}
								</Stack>
								<Stack spacing={2} marginTop={2}>
									<Box>
										<Typography
											sx={{
												fontSize: '12px',
												fontWeight: 500,
												color: '#6b8888',
												mb: 0.8,
											}}>
											Template Name
										</Typography>

										<TextField
											fullWidth
											value={name}
											onChange={e =>
												setName(e.target.value)
											}
											size="small"
											placeholder="e.g. Serious Injury"
											sx={{
												'& .MuiOutlinedInput-root': {
													borderRadius: '12px',
													fontSize: '13px',
													backgroundColor: '#fff',
												},
											}}
											error={nameError?.status}
										/>
										{nameError?.status && (
											<Typography style={helperText}>
												{nameError?.msg}
											</Typography>
										)}
									</Box>
									<Box>
										<Typography
											sx={{
												fontSize: '12px',
												fontWeight: 500,
												color: '#6b8888',
												mb: 0.8,
											}}>
											Template Instruction
										</Typography>

										<TextField
											multiline
											fullWidth
											value={tempInstruction}
											onChange={e =>
												setTempInstruction(
													e.target.value,
												)
											}
											size="small"
											placeholder=""
											minRows={3}
											sx={{
												'& .MuiOutlinedInput-root': {
													borderRadius: '12px',
													fontSize: '13px',
													backgroundColor: '#fff',
												},
											}}
											error={tempInstructionError?.status}
										/>
										{tempInstructionError?.status && (
											<Typography style={helperText}>
												{tempInstructionError?.msg}
											</Typography>
										)}
									</Box>

									<Box sx={{ flexDirection: 'row' }}>
										<Button
											onClick={onExtractTemplate}
											variant="contained"
											disabled={isLoading}
											sx={{
												borderRadius: '8px',
												backgroundColor: teal,
												textTransform: 'none',
												fontWeight: 600,
												fontSize: '13px',
												boxShadow: 'none',
												'&:hover': {
													backgroundColor: teal,
													boxShadow: 'none',
												},
												color: dark,
											}}>
											Add Template
										</Button>
									</Box>
								</Stack>
							</Grid>
						</CardContent>
					</Card>
				</DialogContent>
			</Dialog>
			{isLoading && (
				<ToastBanner
					open={isLoading}
					message={loadingMsg}
					icon={<StarIcon color={'#FFFFFF'} />}
					isCompleted={isGenerationComplete}
				/>
			)}
		</MainGrid>
	);
}
